import React, { useCallback } from "react";
import { toast } from "react-toastify";

import { Stack } from "@shopify/polaris";

import FileUploader, { FileInvalidReason } from "./FileUploader";
import { SystemMessage } from "../../config/SystemMessage";
import ImageUploader from "./ImageUploader";

import styles from "./ArtifactGrid.module.css";
import { useDispatch, useSelector } from "react-redux";
import Asset from "../../types/Asset";
import { AssetState } from "../../types/AssetState";
import { AssetType } from "../../types/AssetType";
import { uploadAssets } from "../../store/actions/Asset";
import InspectionElement from "../../types/InspectionElement";
import { useTranslation } from "react-i18next";
import { containsIllegalCharacters } from "../../validators/validators";
import util from "util";
import ReactGA from "react-ga4";

const MAX_FILE_SIZE = parseInt(
    process.env.REACT_APP_MAX_FILE_SIZE_FOR_IMAGES || "300000"
);
const FILE_REJECTED_MESSAGE_TIMEOUT = 12000;

interface ImageGridProperties {
    elementId: string;
    assetSize: string;
    deactivated: boolean;
}

export default function ImageGrid({
    elementId,
    assetSize,
    deactivated
}: ImageGridProperties) {
    const dispatch = useDispatch();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const localUpdateTime = useSelector(
        (state: any) => state.portal.activeInspection?.localUpdateTime
    );
    const activeInspection = useSelector(
        (state: any) => state.portal.activeInspection
    );
    const { t } = useTranslation("common");
    const user = useSelector((state: any) => state.portal.user);

    let images: any[] = [];
    let maxImages = 0;
    let currentElement: InspectionElement;

    if (activeInspection) {
        currentElement = activeInspection.sections.find(
            (section: any) => section.Id === elementId
        );
        images = currentElement.Images;
        maxImages = currentElement.MaxItems;
    }

    const handleDropZoneDrop = useCallback(
        (_dropFiles, acceptedFiles, _rejectedFiles) => {
            if (_rejectedFiles.length > 0) {
                if (_rejectedFiles[0].rejectReason !== FileInvalidReason.SIZE) {
                    toast.error(
                        t(SystemMessage.INVALID_FILES_FOR_IMAGE_UPLOAD)
                    );
                    ReactGA.event({
                        category: "Image Upload",
                        action: "failed",
                        label: "too large",
                        value: 0
                    });
                    return;
                }

                let rejectedFiles = "";
                for (let i = 0; i !== _rejectedFiles.length; i++) {
                    rejectedFiles += _rejectedFiles[0].name;
                    if (i !== _rejectedFiles.length - 1) {
                        rejectedFiles += " , ";
                    }
                }

                const rejectedFilesMessage = util.format(
                    t(SystemMessage.IMAGE_FILES_TOO_LARGE),
                    String(rejectedFiles)
                );
                toast.error(rejectedFilesMessage);

                ReactGA.event({
                    category: "Image Upload",
                    action: "failed",
                    label: "too large",
                    value: 0
                });
            }

            if (acceptedFiles.length + images.length > maxImages) {
                const maxImagesMessage = util.format(
                    t(SystemMessage.MAX_IMAGES_UPLOADED),
                    String(maxImages)
                );
                toast.error(maxImagesMessage);
                ReactGA.event({
                    category: "Image Upload",
                    action: "failed",
                    label: "max limit reached",
                    value: 0
                });
                return;
            }

            const assets: Asset[] = [];
            for (let i = 0; i !== acceptedFiles.length; i++) {
                if (containsIllegalCharacters(acceptedFiles[i].name)) {
                    toast.error(t(SystemMessage.INVALID_FILE_NAME), {
                        autoClose: FILE_REJECTED_MESSAGE_TIMEOUT
                    });
                    ReactGA.event({
                        category: "Image Upload",
                        action: "failed",
                        label: "invalid filename",
                        value: 0
                    });
                    continue;
                }

                assets.push({
                    name: acceptedFiles[i].name,
                    url: URL.createObjectURL(acceptedFiles[i]),
                    file: acceptedFiles[i],
                    state: AssetState.UPLOADING,
                    progress: 0,
                    type: AssetType.Image,
                    metadata: {},
                    reusable: false,
                    expiryTime: 0
                });
            }

            if (assets.length > 0) {
                dispatch(
                    uploadAssets(activeInspection.Id, currentElement.Id, assets, undefined, user)
                );
            }
        },
        [images]
    );

    const MAX_IMAGES_TO_SHOW = 4;

    const SingleImage = () => {
        if (images.length === 0) {
            const fileUploader = (
                <FileUploader
                    onDrop={handleDropZoneDrop}
                    deactivated={deactivated}
                    maxFileSize={MAX_FILE_SIZE}
                />
            );
            return <div>{fileUploader}</div>;
        } else {
            return (
                <Stack>
                    <ImageUploader
                        id={0}
                        type="single"
                        image={images[0]}
                        elementId={elementId}
                    />
                </Stack>
            );
        }
    };

    const SmallImageGrid = () => {
        const fileUploader = (
            <FileUploader
                onDrop={handleDropZoneDrop}
                deactivated={deactivated}
                maxFileSize={MAX_FILE_SIZE}
            />
        );
        if (images.length === 0) {
            return <div>{fileUploader}</div>;
        } else if (images.length >= MAX_IMAGES_TO_SHOW) {
            const remaining = "+" + (images.length - MAX_IMAGES_TO_SHOW);
            const standardTypeList = ["medium", "small", "remaining", "small"];
            const typeListForMaxImages = ["medium", "small", "small", "small"];
            const typeList =
                images.length === MAX_IMAGES_TO_SHOW
                    ? typeListForMaxImages
                    : standardTypeList;

            const maxStackElements = [];

            for (let i = 0; i !== typeList.length; i++) {
                let remainingNumber = undefined;
                if (typeList[i] === "remaining") {
                    remainingNumber = remaining;
                }

                maxStackElements[i] = (
                    <ImageUploader
                        id={i}
                        key={i}
                        type={typeList[i]}
                        elementId={elementId}
                        remaining={remainingNumber}
                        image={images[i]}
                    />
                );
            }

            return (
                <Stack spacing="tight" alignment="center">
                    {maxStackElements[0]}
                    <Stack vertical spacing="tight">
                        {maxStackElements[1]}
                        {maxStackElements[2]}
                    </Stack>

                    <Stack vertical spacing="tight">
                        {maxStackElements[3]}
                        <div className={styles.smallSection}>
                            {fileUploader}
                        </div>
                    </Stack>
                </Stack>
            );
        } else if (images.length === 1 && maxImages === 1) {
            return (
                <Stack spacing="tight">
                    <ImageUploader
                        id={0}
                        key={0}
                        elementId={elementId}
                        type="medium"
                        image={images[0]}
                    />
                </Stack>
            );
        } else if (images.length === 1) {
            return (
                <Stack spacing="tight">
                    <ImageUploader
                        id={0}
                        key={0}
                        elementId={elementId}
                        type="medium"
                        image={images[0]}
                    />
                    <div className={styles.smallSection}>{fileUploader}</div>
                </Stack>
            );
        } else if (images.length === 2) {
            return (
                <Stack spacing="tight">
                    <Stack spacing="tight">
                        <ImageUploader
                            id={0}
                            key={0}
                            elementId={elementId}
                            type="medium"
                            image={images[0]}
                        />
                    </Stack>
                    <Stack vertical spacing="tight">
                        <ImageUploader
                            id={1}
                            key={1}
                            elementId={elementId}
                            type="small"
                            image={images[1]}
                        />
                        <div className={styles.smallSection}>
                            {fileUploader}
                        </div>
                    </Stack>
                </Stack>
            );
        } else if (images.length === 3) {
            return (
                <Stack spacing="tight">
                    <Stack spacing="tight">
                        <ImageUploader
                            id={0}
                            key={0}
                            elementId={elementId}
                            type="medium"
                            image={images[0]}
                        />
                    </Stack>
                    <Stack vertical spacing="tight">
                        <ImageUploader
                            id={1}
                            key={1}
                            elementId={elementId}
                            type="small"
                            image={images[1]}
                        />
                        <ImageUploader
                            id={2}
                            key={2}
                            elementId={elementId}
                            type="small"
                            image={images[2]}
                        />
                    </Stack>
                    <Stack vertical spacing="tight">
                        <div className={styles.smallSection}>
                            {fileUploader}
                        </div>
                    </Stack>
                </Stack>
            );
        } else {
            return (
                <div>
                    <ImageUploader
                        id={0}
                        key={0}
                        elementId={elementId}
                        type="large"
                        image={images[0]}
                    />
                </div>
            );
        }
    };

    const LargeImageGrid = () => {
        return (
            <Stack>
                {[...images].map((x, i) => (
                    <Stack key={x.url}>
                        <ImageUploader
                            id={i}
                            key={i}
                            type="small"
                            image={x}
                            elementId={elementId}
                        />
                    </Stack>
                ))}
                <div className={styles.smallSection}>
                    <FileUploader
                        onDrop={handleDropZoneDrop}
                        deactivated={deactivated}
                        maxFileSize={MAX_FILE_SIZE}
                    />
                </div>
            </Stack>
        );
    };

    const getGrid = () => {
        if (maxImages === 1) {
            return SingleImage();
        } else if (assetSize === "small") {
            return <SmallImageGrid />;
        } else {
            return LargeImageGrid();
        }
    };

    return <div className={styles.gridPadding}>{getGrid()}</div>;
}
